import React, { useState } from 'react';
import {
  Add as PlusIcon,
  Edit as PencilIcon,
  Delete as TrashIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as XCircleIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon
} from '@mui/icons-material';
import {
  type CompetitorItem,
  addCompetitorAdvantage,
  removeCompetitorAdvantage,
  updateCompetitorAdvantage,
  addCompetitorWeakness,
  removeCompetitorWeakness,
  updateCompetitorWeakness,
  toggleCompetitorStatus
} from '../services/apiService';
import { CompetitorAdvantage, CompetitorWeakness } from '../types/competitor';
import AdvantageModal from './AdvantageModal';
import WeaknessModal from './WeaknessModal';

interface CompetitorManagementProps {
  competitors: CompetitorItem[];
  searchTerm: string;
  onRefresh: () => void;
  onEdit: (competitor: CompetitorItem) => void;
  setError: (error: string | null) => void;
  setSuccess: (success: string | null) => void;
  selectedCompetitorIds: Set<string>;
  onToggleCompetitor: (competitorId: string) => void;
}

const CompetitorManagement: React.FC<CompetitorManagementProps> = ({
  competitors,
  searchTerm,
  onRefresh,
  onEdit,
  setError,
  setSuccess,
  selectedCompetitorIds,
  onToggleCompetitor
}) => {
  const [expandedCompetitors, setExpandedCompetitors] = useState<Set<string>>(new Set());
  const [advantageModal, setAdvantageModal] = useState<{
    isOpen: boolean;
    competitorId: string | null;
    advantage: CompetitorAdvantage | null;
  }>({ isOpen: false, competitorId: null, advantage: null });
  const [weaknessModal, setWeaknessModal] = useState<{
    isOpen: boolean;
    competitorId: string | null;
    weakness: CompetitorWeakness | null;
  }>({ isOpen: false, competitorId: null, weakness: null });

  const toggleExpanded = (competitorId: string) => {
    const newExpanded = new Set(expandedCompetitors);
    if (newExpanded.has(competitorId)) {
      newExpanded.delete(competitorId);
    } else {
      newExpanded.add(competitorId);
    }
    setExpandedCompetitors(newExpanded);
  };

  const handleToggleStatus = async (competitor: CompetitorItem) => {
    try {
      await toggleCompetitorStatus(competitor._id);
      setSuccess(`${competitor.name} status updated`);
      onRefresh();
    } catch (error) {
      setError('Failed to toggle competitor status');
    }
  };

  const handleSaveAdvantage = async (advantage: Partial<CompetitorAdvantage>) => {
    if (!advantageModal.competitorId) return;

    try {
      if (advantageModal.advantage?._id) {
        // Update existing advantage
        await updateCompetitorAdvantage(
          advantageModal.competitorId,
          advantageModal.advantage._id,
          advantage
        );
        setSuccess('Advantage updated successfully');
      } else {
        // Add new advantage
        await addCompetitorAdvantage(advantageModal.competitorId, advantage);
        setSuccess('Advantage added successfully');
      }
      onRefresh();
    } catch (error) {
      setError('Failed to save advantage');
    }
  };

  const handleRemoveAdvantage = async (competitorId: string, advantageId: string) => {
    try {
      await removeCompetitorAdvantage(competitorId, advantageId);
      setSuccess('Advantage removed successfully');
      onRefresh();
    } catch (error) {
      setError('Failed to remove advantage');
    }
  };

  const handleSaveWeakness = async (weakness: Partial<CompetitorWeakness>) => {
    if (!weaknessModal.competitorId) return;

    try {
      if (weaknessModal.weakness?._id) {
        // Update existing weakness
        await updateCompetitorWeakness(
          weaknessModal.competitorId,
          weaknessModal.weakness._id,
          weakness
        );
        setSuccess('Weakness updated successfully');
      } else {
        // Add new weakness
        await addCompetitorWeakness(weaknessModal.competitorId, weakness);
        setSuccess('Weakness added successfully');
      }
      onRefresh();
    } catch (error) {
      setError('Failed to save weakness');
    }
  };

  const handleRemoveWeakness = async (competitorId: string, weaknessId: string) => {
    try {
      await removeCompetitorWeakness(competitorId, weaknessId);
      setSuccess('Weakness removed successfully');
      onRefresh();
    } catch (error) {
      setError('Failed to remove weakness');
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'pricing':
        return '💰';
      case 'features':
        return '⚡';
      case 'implementation':
        return '🔧';
      case 'support':
        return '🤝';
      case 'performance':
        return '📊';
      case 'reliability':
        return '🛡️';
      default:
        return '📌';
    }
  };

  const filteredCompetitors = competitors.filter(competitor => {
    const searchLower = searchTerm.toLowerCase();
    return (
      competitor.name.toLowerCase().includes(searchLower) ||
      competitor.talking_points.toLowerCase().includes(searchLower) ||
      competitor.advantages.some(a => a.point.toLowerCase().includes(searchLower))
    );
  });

  return (
    <>
      <div className="space-y-4">
        {filteredCompetitors.length === 0 ? (
          <div className="text-center py-8 bg-white rounded-lg">
            <p className="text-gray-500">
              {searchTerm
                ? `No competitors found matching "${searchTerm}"`
                : 'No competitors found. Add your first competitor!'}
            </p>
          </div>
        ) : (
          filteredCompetitors.map((competitor) => (
            <div
              key={competitor._id}
              className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
            >
              {/* Competitor Header */}
              <div className="p-4 border-b">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={selectedCompetitorIds.has(competitor._id)}
                      onChange={() => onToggleCompetitor(competitor._id)}
                      className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                      onClick={(e) => e.stopPropagation()}
                    />
                    <button
                      onClick={() => toggleExpanded(competitor._id)}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      {expandedCompetitors.has(competitor._id) ? (
                        <ExpandLessIcon />
                      ) : (
                        <ExpandMoreIcon />
                      )}
                    </button>
                    <h3 className="text-lg font-semibold">{competitor.name}</h3>
                    <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">
                      {competitor.category.replace('_', ' ')}
                    </span>
                    {competitor.isActive ? (
                      <CheckCircleIcon className="text-green-500" fontSize="small" />
                    ) : (
                      <XCircleIcon className="text-gray-400" fontSize="small" />
                    )}
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleToggleStatus(competitor)}
                      className={`px-3 py-1 rounded text-sm ${
                        competitor.isActive
                          ? 'bg-gray-200 hover:bg-gray-300'
                          : 'bg-green-500 text-white hover:bg-green-600'
                      }`}
                    >
                      {competitor.isActive ? 'Deactivate' : 'Activate'}
                    </button>
                    <button
                      onClick={() => onEdit(competitor)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                    >
                      <PencilIcon fontSize="small" />
                    </button>
                  </div>
                </div>
                <p className="mt-2 text-gray-600">{competitor.talking_points}</p>
              </div>

              {/* Expanded Content */}
              {expandedCompetitors.has(competitor._id) && (
                <div className="p-4 space-y-4 bg-gray-50">
                  {/* Advantages */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold flex items-center">
                        <TrendingUpIcon className="mr-2 text-green-600" fontSize="small" />
                        Our Advantages ({competitor.advantages.length})
                      </h4>
                      <button
                        onClick={() => setAdvantageModal({
                          isOpen: true,
                          competitorId: competitor._id,
                          advantage: null
                        })}
                        className="px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600 text-sm"
                      >
                        <PlusIcon fontSize="small" className="mr-1" />
                        Add Advantage
                      </button>
                    </div>
                    <div className="space-y-2">
                      {competitor.advantages.map((advantage) => (
                        <div key={advantage._id} className="flex items-start bg-white p-3 rounded border">
                          <span className="mr-2 text-lg">{getCategoryIcon(advantage.category)}</span>
                          <div className="flex-1">
                            <p className="font-medium">{advantage.point}</p>
                            {advantage.evidence && (
                              <p className="text-sm text-gray-600 mt-1">Evidence: {advantage.evidence}</p>
                            )}
                            <span className="text-xs text-gray-500">{advantage.category}</span>
                          </div>
                          <div className="flex gap-1">
                            <button
                              onClick={() => setAdvantageModal({
                                isOpen: true,
                                competitorId: competitor._id,
                                advantage: advantage as CompetitorAdvantage
                              })}
                              className="text-blue-500 hover:text-blue-700 p-1"
                            >
                              <PencilIcon fontSize="small" />
                            </button>
                            <button
                              onClick={() => handleRemoveAdvantage(competitor._id, advantage._id!)}
                              className="text-red-500 hover:text-red-700 p-1"
                            >
                              <TrashIcon fontSize="small" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Weaknesses */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold flex items-center">
                        <TrendingDownIcon className="mr-2 text-orange-600" fontSize="small" />
                        Their Weaknesses ({competitor.weaknesses.length})
                      </h4>
                      <button
                        onClick={() => setWeaknessModal({
                          isOpen: true,
                          competitorId: competitor._id,
                          weakness: null
                        })}
                        className="px-3 py-1 bg-orange-500 text-white rounded hover:bg-orange-600 text-sm"
                      >
                        <PlusIcon fontSize="small" className="mr-1" />
                        Add Weakness
                      </button>
                    </div>
                    <div className="space-y-2">
                      {competitor.weaknesses.map((weakness) => (
                        <div key={weakness._id} className="flex items-start bg-white p-3 rounded border">
                          <span className="mr-2 text-lg">{getCategoryIcon(weakness.category)}</span>
                          <div className="flex-1">
                            <p className="font-medium">{weakness.point}</p>
                            <span className="text-xs text-gray-500">{weakness.category}</span>
                          </div>
                          <div className="flex gap-1">
                            <button
                              onClick={() => setWeaknessModal({
                                isOpen: true,
                                competitorId: competitor._id,
                                weakness: weakness as CompetitorWeakness
                              })}
                              className="text-blue-500 hover:text-blue-700 p-1"
                            >
                              <PencilIcon fontSize="small" />
                            </button>
                            <button
                              onClick={() => handleRemoveWeakness(competitor._id, weakness._id!)}
                              className="text-red-500 hover:text-red-700 p-1"
                            >
                              <TrashIcon fontSize="small" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Detection Info */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-sm text-blue-800">
                      <strong>Detection:</strong> Competitor mentions are detected using AI-powered LLM matching, which automatically handles misspellings, phonetic variations, and speech-to-text errors.
                    </p>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Advantage Modal */}
      <AdvantageModal
        isOpen={advantageModal.isOpen}
        onClose={() => setAdvantageModal({ isOpen: false, competitorId: null, advantage: null })}
        onSave={handleSaveAdvantage}
        advantage={advantageModal.advantage}
        title={advantageModal.advantage ? 'Edit Advantage' : 'Add Advantage'}
      />

      {/* Weakness Modal */}
      <WeaknessModal
        isOpen={weaknessModal.isOpen}
        onClose={() => setWeaknessModal({ isOpen: false, competitorId: null, weakness: null })}
        onSave={handleSaveWeakness}
        weakness={weaknessModal.weakness}
        title={weaknessModal.weakness ? 'Edit Weakness' : 'Add Weakness'}
      />
    </>
  );
};

export default CompetitorManagement;
